/**
 * @file DashboardHome.js
 * @version 3.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-24
 * @modified 2026-10-30
 *
 * Customizable widget-based dashboard with user-configurable layouts
 *
 * 3.4.0 (2026-10-15): the Data Checks widget is retired (its checks are the
 * contact_data_checks notice) — its fetch of /api/contacts/issues, its contact
 * window and its collapse exception are gone.
 *
 * 3.5.0 (2026-10-30, Cam — rep order requests): a layout may name widgets for
 * a full-width strip ABOVE the grid (`top_strip`, the rep layout's "Request an
 * order"), sized to their content, so the grid's rows are untouched. On the
 * phone the stack is `phone_order` as before.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './DashboardHome.css';
import '../widgets/WidgetWrapper.css';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import { widgetRegistry, layoutPresets, resolveLayout, isWidgetVisibleTo } from '../widgets';
import { badgeOf } from '../utils/permissions';

function DashboardHome({
  user,
  variant = 'desktop',
  xeroConnected,
  checkingXero,
  onReconnect,
  onDisconnect,
  dashboardSettings: settings,
  savingDashboard: saving,
  saveDashboardSettings: saveSettings,
  saveWidgetSettings,
  resetDashboardSettings: resetSettings
}) {

  // Dashboard data states
  const [stats, setStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Resizable heights for right column widgets (local state, not persisted)

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });


  /**
   * Widget ids currently placed, as a sorted string.
   *
   * A string rather than a Set because this is an effect dependency, and
   * widget_placement is a fresh object on every settings fetch — a Set would
   * have a new identity each render and re-fire the effect forever.
   */
  const placedKey = React.useMemo(() => {
    const placement = settings?.widget_placement || {};
    return Object.keys(placement)
      .filter(k => k.startsWith('column_'))
      .flatMap(k => placement[k] || [])
      .sort()
      .join(',');
  }, [settings?.widget_placement]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placedKey]);

  // Initialize collapse buttons for all dashboard cards
  useEffect(() => {
    const initializeCollapse = () => {
      const cards = document.querySelectorAll('.dashboard-card');

      cards.forEach(card => {
        const header = card.querySelector('.card-header h3, .card-header h4');
        if (!header) return;

        // Skip widgets that should not be collapsible
        // The rep widgets are excluded deliberately. This injector appends a
        // raw DOM button into a React-owned <h3>; widgets with static headers
        // tolerate it, but Your Orders re-renders its header (live date, live
        // count) and React can then fail to remove a child it never knew about.
        if (card.classList.contains('email-coverage-card')
          || card.classList.contains('rep-widget')) return;

        // Nor anywhere on a rep's dashboard. Theirs is a fixed layout they
        // cannot rearrange, so collapsing a card is a state they can get into
        // and have no settings screen to get out of. Today's Deliveries is the
        // one that reaches here — it is a shared widget and carries
        // .deliveries-card rather than .rep-widget.
        if (badgeOf(user) === 'REP') return;

        // Skip if button already exists
        if (header.querySelector('.widget-collapse-btn')) return;

        // Create collapse button
        const collapseBtn = document.createElement('button');
        collapseBtn.className = 'widget-collapse-btn btn-icon';
        collapseBtn.innerHTML = '<span class="material-symbols-rounded">expand_more</span>';
        collapseBtn.setAttribute('aria-label', 'Toggle widget');
        collapseBtn.setAttribute('type', 'button');

        // Use addEventListener for better event handling
        const animClasses = ['widget-fading-out', 'widget-shrinking', 'widget-growing', 'widget-fading-in'];
        collapseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Clear any in-progress animation
          card.classList.remove(...animClasses);
          card.style.transition = '';
          card.style.height = '';
          card.style.flex = '';
          card.style.overflow = '';

          if (card.classList.contains('widget-collapsed')) {
            // === EXPANDING ===
            // Measure collapsed height
            const collapsedHeight = card.offsetHeight;

            // Temporarily remove collapsed to measure natural height
            card.classList.remove('widget-collapsed');
            card.classList.add('widget-growing');
            const naturalHeight = card.scrollHeight;

            // Lock at collapsed height — flex must be overridden so explicit height takes effect
            card.style.flex = '0 0 auto';
            card.style.height = collapsedHeight + 'px';
            card.style.overflow = 'hidden';

            // Double rAF ensures browser commits the starting height before transitioning
            requestAnimationFrame(() => { requestAnimationFrame(() => {
              card.style.transition = 'height 0.4s ease-in-out';
              card.style.height = naturalHeight + 'px';

              setTimeout(() => {
                // Phase 2: Fade in content
                card.classList.remove('widget-growing');
                card.classList.add('widget-fading-in');
                card.style.transition = '';
                card.style.height = '';
                card.style.overflow = '';
                card.style.flex = '';

                setTimeout(() => {
                  card.classList.remove('widget-fading-in');
                }, 450);
              }, 420);
            }); });
          } else {
            // === COLLAPSING ===
            // Phase 1: Fade out content
            card.classList.add('widget-fading-out');

            setTimeout(() => {
              // Measure current height before shrinking
              const expandedHeight = card.offsetHeight;
              const headerEl = card.querySelector('.card-header');
              const cardStyle = getComputedStyle(card);
              const padTop = parseFloat(cardStyle.paddingTop);
              const padBottom = parseFloat(cardStyle.paddingBottom);
              const collapsedHeight = headerEl.offsetHeight + padTop + padBottom;

              // Phase 2: Smoothly shrink card height — override flex so explicit height works
              card.classList.remove('widget-fading-out');
              card.classList.add('widget-shrinking');
              card.style.flex = '0 0 auto';
              card.style.height = expandedHeight + 'px';
              card.style.overflow = 'hidden';

              requestAnimationFrame(() => { requestAnimationFrame(() => {
                card.style.transition = 'height 0.4s ease-in-out';
                card.style.height = collapsedHeight + 'px';

                setTimeout(() => {
                  // Final collapsed state
                  card.classList.remove('widget-shrinking');
                  card.classList.add('widget-collapsed');
                  card.style.transition = '';
                  card.style.height = '';
                  card.style.overflow = '';
                  card.style.flex = '';
                }, 450);
              }); });
            }, 420);
          }
        });

        header.appendChild(collapseBtn);

        // Apply default collapsed state on first initialization
        const wrapper = card.closest('[data-widget-id]');
        const widgetId = wrapper?.dataset?.widgetId;
        if (widgetId && !card.dataset.collapseInitialized) {
          card.dataset.collapseInitialized = 'true';
          if (settings.widget_settings?.[widgetId]?.defaultCollapsed) {
            card.classList.add('widget-collapsed');
          }
        }
      });
    };

    // Not on a phone: the stack scrolls, so there is nothing to collapse, and
    // the height animation would fight it.
    if (variant === 'phone') return undefined;

    // Initialize immediately and after a short delay for dynamic content
    initializeCollapse();
    const timer = setTimeout(initializeCollapse, 100);

    return () => clearTimeout(timer);
  }, [settings.widget_placement, dataLoading, variant]);

  const fetchData = async () => {
    setDataLoading(true);
    // This endpoint backs the contacts widgets only. A layout without them
    // — every rep layout, for one — should not pay for the round trip.
    const placed = new Set(placedKey ? placedKey.split(',') : []);
    const wants = (...ids) => ids.some(id => placed.has(id));
    if (wants('email_coverage', 'customer_chart')) await fetchStats();
    setDataLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Handle widget settings change (zoom, etc.)
  const handleWidgetSettingsChange = (widgetId, newSettings) => {
    saveWidgetSettings(widgetId, newSettings);
  };


  // Handle save from configurator

  const getWidgetRowSpan = (widgetId) => {
    return settings.widget_settings?.[widgetId]?.rowSpan || null; // null = auto
  };

  // Check if a widget is set to span multiple columns
  const getWidgetColSpan = (widgetId) => {
    return settings.widget_settings?.[widgetId]?.colSpan || 1;
  };

  // Get the grid-column start for a spanning widget (accounts for direction)
  const getSpanGridStart = (widgetId, assignedColumn) => {
    const ws = settings.widget_settings?.[widgetId] || {};
    const colSpan = ws.colSpan || 1;
    const direction = ws.spanDirection || 'right';
    if (direction === 'left') {
      return Math.max(0, assignedColumn - (colSpan - 1));
    }
    return assignedColumn;
  };

  // Build a flat grid of items, interleaving spanning and non-spanning row by row.
  // Each widget gets an explicit grid row and column so spanning widgets appear
  // at whatever position the user placed them.
  const buildGridLayout = (layout) => {
    const numCols = layout.columns.length;
    // Drop ids the registry no longer knows. renderWidget already returns null
    // for one, but buildGridLayout would still allocate it a grid cell — so a
    // retired widget left a hole in the layout of everyone whose saved
    // widget_placement still named it, which is every user until they next open
    // the configurator. Filtering here is what makes retiring a widget safe.
    const colLists = Array.from({ length: numCols }, (_, i) =>
      (settings.widget_placement?.[`column_${i}`] || []).filter(id => widgetRegistry[id])
    );
    const maxLen = Math.max(...colLists.map(l => l.length), 0);
    const items = [];
    const colPointers = Array(numCols).fill(0);
    const blockedUntil = Array(numCols).fill(0); // row until which col is blocked by a span

    for (let row = 0; row < maxLen + numCols; row++) {
      let anyOutput = false;
      for (let col = 0; col < numCols; col++) {
        if (blockedUntil[col] > row) continue;
        const idx = colPointers[col];
        if (idx >= colLists[col].length) continue;

        const widgetId = colLists[col][idx];
        const span = getWidgetColSpan(widgetId);
        const startCol = span > 1 ? getSpanGridStart(widgetId, col) : col;
        const clampedSpan = Math.min(span, numCols - startCol);

        // Check if all columns this would span are free
        let canPlace = true;
        for (let c = startCol; c < startCol + clampedSpan; c++) {
          if (c !== col && blockedUntil[c] > row) { canPlace = false; break; }
        }
        if (!canPlace) continue;

        items.push({
          widgetId, homeCol: col, gridColumn: startCol + 1,
          gridColumnSpan: clampedSpan, row: row + 1
        });
        colPointers[col]++;
        anyOutput = true;

        // Block covered columns for next row
        if (clampedSpan > 1) {
          for (let c = startCol; c < startCol + clampedSpan; c++) {
            if (c !== col) blockedUntil[c] = row + 1;
          }
        }
      }
      if (!anyOutput && colPointers.every((p, i) => p >= colLists[i].length)) break;
    }

    // Determine which columns are covered by a col-spanning widget from another column
    const coveredBySpan = new Set();
    items.forEach(({ homeCol, gridColumn, gridColumnSpan }) => {
      if (gridColumnSpan > 1) {
        const startCol = gridColumn - 1;
        for (let c = startCol; c < startCol + gridColumnSpan; c++) {
          if (c !== homeCol) coveredBySpan.add(c);
        }
      }
    });

    // Assign manual rowSpans first; mark auto items as null
    items.forEach(item => {
      const manual = getWidgetRowSpan(item.widgetId);
      item.rowSpan = coveredBySpan.has(item.homeCol) ? 1 : (manual || null);
    });

    // Compute effective totalRows: max of natural row count and any column's min required rows
    let totalRows = items.length > 0 ? Math.max(...items.map(i => i.row)) : 1;
    for (let col = 0; col < numCols; col++) {
      if (coveredBySpan.has(col)) continue;
      const colItems = items.filter(i => i.homeCol === col);
      const manualSum = colItems.reduce((s, i) => s + (i.rowSpan || 0), 0);
      const autoCount = colItems.filter(i => i.rowSpan === null).length;
      totalRows = Math.max(totalRows, manualSum + autoCount);
    }

    // Distribute remaining rows among auto-span widgets per column, then set row positions
    for (let col = 0; col < numCols; col++) {
      const colItems = items.filter(i => i.homeCol === col).sort((a, b) => a.row - b.row);
      if (colItems.length === 0) continue;

      if (coveredBySpan.has(col)) {
        colItems.forEach(i => { if (i.rowSpan === null) i.rowSpan = 1; });
        continue;
      }

      const manualSum = colItems.reduce((s, i) => s + (i.rowSpan || 0), 0);
      const autoItems = colItems.filter(i => i.rowSpan === null);
      const remaining = Math.max(totalRows - manualSum, autoItems.length);

      if (autoItems.length > 0) {
        const base = Math.floor(remaining / autoItems.length);
        const extra = remaining % autoItems.length;
        autoItems.forEach((item, idx) => {
          item.rowSpan = idx < extra ? base + 1 : base;
        });
      }

      // Recompute row positions from rowSpans
      let currentRow = 1;
      colItems.forEach(item => {
        item.row = currentRow;
        currentRow += item.rowSpan;
      });
    }

    // For each column, count how many grid rows it occupies
    const colRowCounts = Array(numCols).fill(0);
    items.forEach(({ gridColumn, gridColumnSpan }) => {
      const startCol = gridColumn - 1;
      for (let c = startCol; c < startCol + gridColumnSpan; c++) {
        if (c < numCols) colRowCounts[c]++;
      }
    });

    return { items, totalRows, colRowCounts, numCols };
  };

  // Render a single widget by ID (wrapped with data-widget-id)
  const renderWidget = (widgetId, columnType, columnIndex) => {
    const widget = widgetRegistry[widgetId];
    if (!widget) return null;
    // The configurator filters its picker, but a saved placement can still name
    // a widget this user is not entitled to (a demoted admin, a hand-edited
    // row). Enforce here too — the picker is convenience, this is the check.
    if (!isWidgetVisibleTo(widget, { badge: badgeOf(user), isAdmin: !!user?.is_admin })) return null;

    const WidgetComponent = widget.component;
    const widgetSettings = settings.widget_settings?.[widgetId] || {};

    // Common props for all widgets
    const commonProps = {
      settings: widgetSettings,
      onSettingsChange: (newSettings) => handleWidgetSettingsChange(widgetId, newSettings),
      columnType,
      variant,
      // So a widget can drop chrome a rep has no use for, on the laptop as well
      // as the phone, without each one re-deriving this from the badge.
      isRep: badgeOf(user) === 'REP'
    };

    let rendered;

    // Widget-specific props
    switch (widgetId) {
      case 'clock':
      case 'calendar':
        rendered = <WidgetComponent {...commonProps} />;
        break;

      case 'xero_status':
        rendered = (
          <WidgetComponent
            {...commonProps}
            xeroConnected={xeroConnected}
            checkingXero={checkingXero}
            onReconnect={onReconnect}
            onDisconnect={onDisconnect}
          />
        );
        break;

      case 'email_coverage':
        rendered = (
          <WidgetComponent
            {...commonProps}
            stats={stats}
            loading={dataLoading}
          />
        );
        break;

      case 'delivery_heatmap':
        rendered = <WidgetComponent {...commonProps} />;
        break;

      case 'customer_chart':
        rendered = (
          <WidgetComponent
            {...commonProps}
            stats={stats}
            loading={dataLoading}
          />
        );
        break;

      default:
        rendered = <WidgetComponent {...commonProps} />;
    }

    return (
      <div data-widget-id={widgetId} key={widgetId} style={{ display: 'contents' }}>
        {rendered}
      </div>
    );
  };

  // Get current layout configuration
  const currentLayout = resolveLayout(settings);
  const { items: gridItems, totalRows } = buildGridLayout(currentLayout);

  /*
   * Phone: bypass the grid entirely rather than trying to make explicit
   * gridRow/gridColumn/rowSpan/colSpan placement degrade to a single column —
   * that is far harder than not running it. The columns are concatenated in
   * order, so column_0 ++ column_1 ++ column_2 IS the phone reading order.
   * config/repDashboardLayout.js is written with that in mind.
   */
  if (variant === 'phone') {
    // An explicit phone_order wins. Without it the columns are concatenated,
    // which is the older behaviour and means the DESKTOP arrangement decides
    // the phone reading order — rearranging the grid silently reorders every
    // phone, for reasons that have nothing to do with phones.
    const stack = (settings.phone_order?.length
      ? settings.phone_order
      : [0, 1, 2].flatMap(i => settings.widget_placement?.[`column_${i}`] || [])
    ).filter(id => widgetRegistry[id]);
    return (
      <div className="dashboard-home dashboard-home-stack">
        {stack.map(id => (
          <div className="widget-cell" key={id}>
            {renderWidget(id, 'wide', 0)}
          </div>
        ))}
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="dashboard-home">
        <div className="dashboard-loading">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Widgets above the grid, full width and as tall as their content (the rep
  // layout's "Request an order"). The grid below keeps every row it had.
  const topStrip = (settings.top_strip || []).filter(id => widgetRegistry[id]);

  return (
    <div className={`dashboard-home${topStrip.length ? ' has-top-strip' : ''}`}>
      {topStrip.length > 0 && (
        <div className="dashboard-top-strip">
          {topStrip.map(id => (
            <div className="top-strip-cell" key={id}>{renderWidget(id, 'wide', 0)}</div>
          ))}
        </div>
      )}
      {/* Flat grid with explicit row/column placement */}
      <div
        className="dashboard-home-content"
        data-layout={settings.layout_preset}
        style={{
          gridTemplateColumns: currentLayout.gridTemplate,
          gridTemplateRows: `repeat(${totalRows}, 1fr)`
        }}
      >
        {gridItems.map(({ widgetId, homeCol, gridColumn, gridColumnSpan, row, rowSpan }) => {
          const isSpanning = gridColumnSpan > 1;
          const gridRowValue = rowSpan > 1 ? `${row} / span ${rowSpan}` : row;

          return (
            <div
              key={widgetId}
              className={isSpanning ? 'spanning-widget-wrapper' : 'widget-cell'}
              data-column-type={currentLayout.columns[homeCol]?.type}
              style={{
                gridColumn: isSpanning ? `${gridColumn} / span ${gridColumnSpan}` : gridColumn,
                gridRow: gridRowValue
              }}
            >
              {renderWidget(widgetId, currentLayout.columns[homeCol]?.type, homeCol)}
            </div>
          );
        })}
      </div>

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />
    </div>
  );
}

export default DashboardHome;
